'use client';

import { useEffect, useRef, useState, lazy, Suspense } from 'react';
import { Link } from '@/components/shared/Link';
import { UploadedImage } from '@/components/shared/UploadedImage';
import { PageHero } from '@/components/shared/PageHero';
import { SectionLabel } from '@/components/shared/SectionLabel';
import { useTranslations } from '@/i18n/intl';
import { stripHtml, parseSettingsJson } from '@/lib/utils';
import { RichText } from '@/components/shared/RichText';
import dynamicIconImports from 'lucide-react/dynamicIconImports';
import {
  Shield,
  Lightbulb,
  Users,
  Award,
  Target,
  Heart,
  Briefcase,
  TrendingUp,
  Star,
  Zap,
  Globe,
  Anchor,
  LucideProps,
  Linkedin,
  Mail
} from 'lucide-react';

interface IconProps extends LucideProps {
  name: keyof typeof dynamicIconImports;
}

const iconCache: Record<string, React.LazyExoticComponent<React.ComponentType<LucideProps>>> = {};

const DynamicIcon = ({ name, ...props }: IconProps) => {
  if (!iconCache[name]) {
    iconCache[name] = lazy(dynamicIconImports[name]);
  }
  const LucideIcon = iconCache[name];
  return (
    <Suspense fallback={<div className="w-8 h-8 bg-gray-100 animate-pulse rounded-sm" />}>
      <LucideIcon {...props} />
    </Suspense>
  );
};

interface AboutUsClientProps {
  settings: Record<string, string>;
  people: any[];
  locale: string;
}

export default function AboutUsClient({ settings, people, locale }: AboutUsClientProps) {
  const t = useTranslations('About');
  const tCommon = useTranslations('Common');
  const [statsVisible, setStatsVisible] = useState(false);
  const statsRef = useRef<HTMLDivElement>(null);
  const values = parseSettingsJson<{ title: string; description: string; icon?: string }[]>(
    settings.about_values,
    []
  );
  const stats = (() => {
    const aboutStats = parseSettingsJson<{ label: string; value: number | string; suffix: string }[]>(
      settings.about_stats,
      []
    );
    if (aboutStats.length > 0) return aboutStats;
    return parseSettingsJson(settings.home_stats, []);
  })();

  const getValueIcon = (title: string, iconName?: string) => {
    if (iconName && dynamicIconImports[iconName as keyof typeof dynamicIconImports]) {
      return <DynamicIcon name={iconName as keyof typeof dynamicIconImports} className="w-8 h-8" />;
    }

    const t = title.toLowerCase();
    const className = "w-8 h-8";
    if (t.includes('integrity')) return <Shield className={className} />;
    if (t.includes('innovation') || t.includes('thought')) return <Lightbulb className={className} />;
    if (t.includes('collaboration') || t.includes('teamwork')) return <Users className={className} />;
    if (t.includes('excellence') || t.includes('quality')) return <Award className={className} />;
    if (t.includes('impact') || t.includes('result') || t.includes('focus') || t.includes('outcome')) return <Target className={className} />;
    if (t.includes('client') || t.includes('customer') || t.includes('heart')) return <Heart className={className} />;
    if (t.includes('professional') || t.includes('business')) return <Briefcase className={className} />;
    if (t.includes('growth')) return <TrendingUp className={className} />;
    if (t.includes('speed') || t.includes('agility') || t.includes('built')) return <Zap className={className} />;
    if (t.includes('global')) return <Globe className={className} />;
    if (t.includes('anchor') || t.includes('stable')) return <Anchor className={className} />;
    return <Star className={className} />;
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setStatsVisible(true);
      },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (window.location.hash !== '#our-team') return;
    const scrollToTeam = () => {
      document.getElementById('our-team')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    scrollToTeam();
    const t = window.setTimeout(scrollToTeam, 100);
    return () => window.clearTimeout(t);
  }, []);

  const teamMembers = [...people].sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0)
  );

  return (
    <>
      <section className="py-10 mt-20 md:py-[72px] px-5 md:px-[40px] bg-white">
        <div className="max-w-[1170px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <div className="aspect-square max-w-[550px] rounded-lg overflow-hidden bg-navy shadow-xl border border-light">
              <UploadedImage
                src={settings.about_hero_image || "/images/simisi/page3_img1.png"}
                alt="Simisi&Co."
                width={550}
                height={550}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
          <div>
            <SectionLabel text={t('title')} />
            <RichText
              html={settings.about_headline || ''}
              className="text-[28px] md:text-[32px] font-heading font-semibold text-navy leading-tight mb-6 [&_span]:text-primary [&_p]:mb-0"
            />
            <RichText
              html={settings.about_description || ''}
              className="text-[16px] font-sans text-gray-600 leading-relaxed mb-4 [&_p]:mb-4"
            />
          </div>
        </div>
      </section>

      {values.length > 0 && (
        <section className="py-20 md:py-32 px-5 md:px-[40px] bg-[#F9F9F9]">
          <div className="max-w-[1240px] mx-auto">
            <div className="text-center mb-16">
              <SectionLabel text={t('ourValues')} className="text-center" />
              <h2
                className="text-[32px] md:text-[45px] font-heading font-bold text-navy leading-tight"
              >
                {t('valuesTitle')}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
              {values.map((v: any, i: number) => (
                <div
                  key={i}
                  className="group bg-white p-10 md:p-12 rounded-2xl shadow-sm border border-gray-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-500 flex flex-col items-center text-center"
                >
                  <div className="w-20 h-20 rounded-2xl bg-primary/5 flex items-center justify-center mb-8 group-hover:bg-primary group-hover:rotate-[10deg] transition-all duration-500">
                    <div className="text-primary group-hover:text-white transition-colors duration-500">
                      {getValueIcon(v.title, v.icon)}
                    </div>
                  </div>
                  <h3 className="text-[24px] md:text-[26px] font-heading font-bold text-navy mb-6">
                    {v.title}
                  </h3>
                  <RichText
                    html={v.description || ''}
                    className="text-[16px] font-sans text-gray-500 leading-relaxed [&_p]:mb-4"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {stats.length > 0 && (
        <section ref={statsRef} className="py-10 md:py-[72px] px-5 md:px-[40px] bg-white">
          <div className="max-w-[1170px] mx-auto">
            <SectionLabel text={t('achievements')} />
            <h2 className="text-[32px] md:text-[40px] font-heading font-normal text-navy leading-snug mb-12">
              {t('achievementsTitle')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-[52px] font-heading font-semibold text-primary mb-2">
                    {statsVisible ? (
                      <StatCounter target={stat.value} suffix={stat.suffix} />
                    ) : (
                      <span>0{stat.suffix}</span>
                    )}
                  </div>
                  <p className="text-[24px] font-heading text-navy/80">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {teamMembers.length > 0 && (
        <section id="our-team" className="py-16 md:py-24 px-5 md:px-[40px] bg-white scroll-mt-28">
          <div className="max-w-[1240px] mx-auto">
            <h2 className="text-[40px] md:text-[56px] font-heading font-normal text-navy mb-16 border-b border-gray-100 pb-8">
              {t('ourTeam')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-8 gap-y-16">
              {teamMembers.map((person: any) => (
                <div key={person.slug} className="flex flex-col sm:flex-row lg:flex-col xl:flex-row gap-6 items-start w-full">
                  <div className="flex flex-col items-start gap-4 shrink-0 w-full sm:w-auto lg:w-full xl:w-auto">
                    <div className="w-full sm:w-[180px] lg:w-full xl:w-[180px] aspect-square bg-gray-100 overflow-hidden rounded-sm relative">
                      <UploadedImage
                        src={person.image || '/images/simisi/placeholder.png'}
                        alt={person.name}
                        fill
                        className="object-cover object-top transition-all duration-500"
                      />
                    </div>
                    <div className="flex gap-4 items-center">
                      <a
                        href={person.linkedin_url || "#"}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-400 hover:text-[#0077B5] transition-colors"
                      >
                        <Linkedin size={20} />
                      </a>
                      <a
                        href={`mailto:${person.email || 'info@simisi.id'}`}
                        className="text-gray-400 hover:text-primary transition-colors sm:hidden xl:hidden"
                      >
                        <Mail size={20} />
                      </a>
                    </div>
                  </div>

                  <div className="flex-1 pt-1 w-full">
                    <Link
                      href={`/our-people/${person.slug}`}
                      className="block group"
                    >
                      <h4 className="text-[18px] md:text-[20px] font-bold text-navy leading-tight group-hover:text-primary transition-colors">
                        {person.name}
                      </h4>
                    </Link>
                    <p className="text-[14px] text-gray-600 mt-2 mb-4 leading-relaxed font-sans">
                      {stripHtml(person.position || '')}
                    </p>
                    <a
                      href={`mailto:${person.email || 'info@simisi.id'}`}
                      className="hidden sm:inline-flex lg:hidden xl:inline-flex text-[13px] font-bold text-navy underline decoration-2 underline-offset-4 hover:text-primary hover:decoration-primary transition-all uppercase tracking-wider"
                    >
                      Email
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}

function StatCounter({ target, suffix }: { target: number | string; suffix: string }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    const targetValue = Number(target);
    if (isNaN(targetValue) || targetValue <= 0) {
      setCount(targetValue || 0);
      return;
    }

    let start = 0;
    const duration = 2000;
    const increment = targetValue / (duration / 16);

    const timer = setInterval(() => {
      start += increment;
      if (start >= targetValue) {
        setCount(targetValue);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);

    return () => clearInterval(timer);
  }, [target]);

  return (
    <span>
      {count}
      {suffix}
    </span>
  );
}
